import Breakout from "@/features/Common/components/Breakout";
import DefaultError from "@/features/Common/components/DefaultError";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import OneVoucherRedemption from "@/features/Common/components/OneVoucherRedemption";
import PaymentFailure from "@/features/Common/components/PaymentFailure";
import PaymentOptionSelection from "@/features/Common/components/PaymentOptionsSelection";
import PaymentReceipt from "@/features/Common/components/PaymentReceipt";
import PathologyPanelDetails from "@/features/Pathology/components/PathologyPanelDetails";
import PathologyPanels from "@/features/Pathology/components/PathologyPanels";
import {
  getVoucherDownload,
  getVoucherPaymentRedirectURL,
  MemberProductPaymentRatesChargeCode,
  MemberProductPaymentRatesItem,
  MemberServiceOfferingType,
  PathologyPanel,
  VoucherSubType,
  VoucherType,
} from "@/services/core-api-adapter";
import { useGlobalStore } from "@/store";
import { Stack, Typography } from "@mui/material";
import { sendEvent } from "@unu/ui-common";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { useMachine } from "@xstate/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import AddOrUseVoucherPage from "../AddOrUseVoucher";
import AddToProfileModal from "../AddToProfileModal";
import { BuyVoucherPrompt } from "../BuyVoucherPrompt";
import { DoctorNurseVoucherTypeSelection } from "../DoctorNurseVoucherTypeSelection";
import VoucherCodeModal from "../VoucherCodeModal";
import VoucherOrderSummary from "../VoucherOrderSummary";
import { eventNames, voucheringFlowStateMachine } from "./machine";
import { formatAmount } from "@/utils";

export default function VoucheringFlow() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const isMyVouchers = searchParams.get("isMyVouchers");
  const [isDownloadingVoucherFile, setIsDownloadingVoucherFile] =
    useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useGlobalStore();

  const fromMyPurchasesPage = location.state?.fromMyPurchasesPage;

  const [voucheringStateMachine, voucheringStateMachineDispatch] = useMachine(
    voucheringFlowStateMachine,
    {
      input: {
        context: {
          currentUserMemberId: state.currentUser.memberId || null,
          orderId: orderId || null,
          isMyVouchers: isMyVouchers || null,
          fromMyPurchasesPage: fromMyPurchasesPage,
        },
      },
    } as any
  );

  function onGoHomeButtonClick() {
    voucheringStateMachineDispatch({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onDoctorNurseConsultationVoucherClick(voucherType: VoucherType) {
    voucheringStateMachineDispatch({
      type: eventNames.DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED,
      input: voucherType,
    });
  }

  function onDoctorVoucherClick(voucherSubType: VoucherSubType) {
    voucheringStateMachineDispatch({
      type: eventNames.DOCTOR_VOUCHER_CLICKED,
      input: voucherSubType,
    });
  }

  function onDocMedBundleConsultClick(voucherSubType: VoucherSubType) {
    voucheringStateMachineDispatch({
      type: eventNames.DOCTOR_MEDICINE_VOUCHER_CLICKED,
      input: voucherSubType,
    });
  }

  function onBloodPanelClick(panel: PathologyPanel) {
    voucheringStateMachineDispatch({
      type: eventNames.BLOOD_PANEL_CLICKED,
      output: panel,
    });
  }

  function onBuyNowButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.BUY_NOW_BUTTON_CLICKED,
    });
  }

  function onCardPaymentButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.CARD_PAYMENT_BUTTON_CLICKED,
    });
  }

  function onPathologyTestVoucherClick(voucherType: VoucherType) {
    voucheringStateMachineDispatch({
      type: eventNames.PATHOLOGY_VOUCHER_CLICKED,
      input: voucherType,
    });
  }

  function onApplyVoucherClick(voucherCode: string) {
    voucheringStateMachineDispatch({
      type: eventNames.APPLY_VOUCHER_CLICKED,
      input: voucherCode,
    });
  }

  function onContinueButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
    });
  }

  function onNurseVoucherClick(voucherSubType: VoucherSubType) {
    voucheringStateMachineDispatch({
      type: eventNames.NURSE_VOUCHER_CLICKED,
      input: voucherSubType,
    });
  }

  function onBackButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.BACK_BUTTON_CLICKED,
    });
  }

  function onOneVoucherContinueButtonClick(oneVoucherPin: string) {
    voucheringStateMachineDispatch({
      type: eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED,
      input: oneVoucherPin,
    });
  }

  function onViewPaymentReceiptClick() {
    voucheringStateMachineDispatch({
      type: eventNames.VIEW_RECEIPT_BUTTON_CLICKED,
    });
  }

  function onSaveToProfileClick() {
    voucheringStateMachineDispatch({
      type: eventNames.SAVE_TO_PROFILE_BUTTON_CLICKED,
    });
  }

  async function onShareVoucherClick() {
    setIsDownloadingVoucherFile(true);

    const voucherDetails =
      voucheringStateMachine.context.paymentReceiptResponse;
    if (!voucherDetails) {
      setIsDownloadingVoucherFile(false);
      return;
    }

    const voucherOrderId =
      voucheringStateMachine.context.initiatePaymentResponse?.voucherOrderId ||
      orderId;
    if (!voucherOrderId) {
      console.error("Voucher order ID not found");
      setIsDownloadingVoucherFile(false);
      return;
    }

    try {
      const blob = await getVoucherDownload(voucherOrderId);

      const contentType = blob.type;
      let fileExtension = "pdf";
      if (contentType.includes("png")) {
        fileExtension = "png";
      }

      const fileName = `${voucherDetails.voucherSubTypeName}_Voucher_${voucherDetails.voucherCode}.${fileExtension}`;
      const file = new File([blob], fileName, { type: contentType });

      try {
        const shareData = {
          files: [file],
          title: `${voucherDetails.voucherSubTypeName} Voucher`,
          text: `${voucherDetails.voucherSubTypeName} Voucher - ${voucherDetails.voucherCode}`,
        };

        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setIsDownloadingVoucherFile(false);
          return;
        } else if (NativeBridgeAvailable) {
          downloadFile(blob, fileName);
          setIsDownloadingVoucherFile(false);
        }
      } catch (shareError) {
        console.error("Share failed:", shareError);
      }

      // If native sharing is not available or failed, download the file
      downloadFile(blob, fileName);
    } catch (error: any) {
      console.error(`Error fetching or sharing voucher PDF: ${error.message}`);
    } finally {
      setIsDownloadingVoucherFile(false);
    }
  }

  // Helper function to download the file
  function downloadFile(blob: Blob, fileName: string) {
    const isMobileApp = NativeBridgeAvailable;

    if (isMobileApp) {
      const reader = new FileReader();
      reader.onloadend = function () {
        const dataUrl = reader.result as string;
        sendEvent({
          name: BridgeEvent.SendData,
          data: {
            name: BridgeEvent.ShareFile,
            data: {
              fileName,
              mimeType: blob.type,
              dataUrl: dataUrl,
            },
          },
        });
      };
      reader.readAsDataURL(blob);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  function onVoucherPaymentContinueButtonClick(voucherCode?: string) {
    voucheringStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
      input: voucherCode,
    });
  }

  function onUseVoucherClick() {
    voucheringStateMachineDispatch({
      type: eventNames.USE_VOUCHER_BUTTON_CLICKED,
    });
  }

  function onUseVoucherNowClick() {
    if (
      voucheringStateMachine.context.applyVoucherResponse?.memberOfferingUsage
        ?.serviceOfferingType === MemberServiceOfferingType.PATHOLOGY
    ) {
      navigate("/home/get-medical-assistance/pathology?voucher=true");
    } else {
      navigate("/home/get-medical-assistance/intercare?voucher=true");
    }
  }

  function onBuyVoucherClick() {
    voucheringStateMachineDispatch({
      type: eventNames.BUY_VOUCHER_BUTTON_CLICKED,
    });
  }

  function onStartDoctorConsultationClick() {
    navigate("/home/get-medical-assistance/intercare");
    voucheringStateMachineDispatch({
      type: eventNames.USER_REDIRECTED,
    });
  }

  function onOneVoucherButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.ONE_VOUCHER_BUTTON_CLICKED,
    });
  }

  function onBreakoutDisclaimerContinue() {
    voucheringStateMachineDispatch({ type: eventNames.USER_REDIRECTED });
  }

  function onGetSupportClick() {
    navigate("/home/more/get-support");
    voucheringStateMachineDispatch({
      type: eventNames.USER_REDIRECTED,
    });
  }

  function getPaymentRedirectUrl() {
    const redirectURL = getVoucherPaymentRedirectURL(
      voucheringStateMachine.context.initiatePaymentResponse?.voucherOrderId ||
        ""
    );

    return redirectURL;
  }

  const directToDoctorPaymentRates =
    voucheringStateMachine.context?.membersProfileResponse?.consultation?.consultationRates.find(
      (rate: MemberProductPaymentRatesItem) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  return (
    <>
      {(voucheringStateMachine.matches("initiatingPayment") ||
        voucheringStateMachine.matches("fetchingBloodPanelList") ||
        voucheringStateMachine.matches("fetchingBloodPanelDetails") ||
        voucheringStateMachine.matches("fetchingPaymentReceipt") ||
        voucheringStateMachine.matches("waitingForPaymentOutcome") ||
        voucheringStateMachine.matches("processingPaymentOutcome") ||
        voucheringStateMachine.matches("applyingVoucherCode") ||
        voucheringStateMachine.matches("fetchingVoucherTypes") ||
        voucheringStateMachine.matches("initiatingOneVoucherRedemption")) && (
        <FullscreenLoadingIndicator
          loadingText={t("common.pleaseWait")}
          mainIconType="face"
        />
      )}

      {voucheringStateMachine.matches("buyVoucher") && (
        <AddOrUseVoucherPage
          voucher={voucheringStateMachine.context?.vouchers}
          onDoctorNurseConsultationVoucherClicked={
            onDoctorNurseConsultationVoucherClick
          }
          onPathologyTestVoucherClicked={onPathologyTestVoucherClick}
          onGetSupportClicked={onGetSupportClick}
          onApplyVoucherClicked={onApplyVoucherClick}
          applyVoucherError={voucheringStateMachine.context?.applyVoucherError}
          onUseVoucherClicked={onUseVoucherClick}
          onBuyVoucherClicked={onBuyVoucherClick}
          voucherCode={voucheringStateMachine.context?.voucherCode}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {(voucheringStateMachine.matches("promptingVoucherCode") ||
        voucheringStateMachine.matches("useVoucher")) && (
        <AddOrUseVoucherPage
          preselectedTab="use-voucher"
          voucher={voucheringStateMachine.context?.vouchers}
          onDoctorNurseConsultationVoucherClicked={
            onDoctorNurseConsultationVoucherClick
          }
          onPathologyTestVoucherClicked={onPathologyTestVoucherClick}
          onGetSupportClicked={onGetSupportClick}
          onApplyVoucherClicked={onApplyVoucherClick}
          applyVoucherError={voucheringStateMachine.context?.applyVoucherError}
          onUseVoucherClicked={onUseVoucherClick}
          onBuyVoucherClicked={onBuyVoucherClick}
          voucherCode={voucheringStateMachine.context?.voucherCode}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("doctorNurseConsultSelection") && (
        <DoctorNurseVoucherTypeSelection
          onDoctorConsultClick={onDoctorVoucherClick}
          onNurseConsultClick={onNurseVoucherClick}
          onDocMedBundleConsultClick={onDocMedBundleConsultClick}
          selectedVoucher={voucheringStateMachine.context?.selectedVoucherType}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("showingBloodPanelList") && (
        <PathologyPanels
          panelList={voucheringStateMachine.context.bloodPanelListResponse}
          onPanelClick={onBloodPanelClick}
          onBackButtonClick={onBackButtonClick}
          onStartDoctorConsultationClick={onStartDoctorConsultationClick}
          consultAmountFormatted={
            directToDoctorPaymentRates?.amountFormatted || ""
          }
          consultOriginalAmountFormatted={
            directToDoctorPaymentRates?.originalAmountFormatted || ""
          }
          isDiscountApplied={false}
          isVoucherSelected={true}
        />
      )}

      {voucheringStateMachine.matches("showingBloodPanelDetails") &&
        voucheringStateMachine.context.selectedBloodPanel && (
          <PathologyPanelDetails
            onBuyButtonClick={onBuyNowButtonClick}
            onBackButtonClick={onBackButtonClick}
            isVoucherSelected={true}
            panel={voucheringStateMachine.context.selectedBloodPanel}
          />
        )}

      {voucheringStateMachine.matches("showingPrompt") && (
        <BuyVoucherPrompt
          voucherVendorDefinitionId={
            voucheringStateMachine.context?.selectedVoucherSubType
              ?.voucherVendorDefinitionId
          }
          onContinueButtonClick={onVoucherPaymentContinueButtonClick}
          onBackButtonClick={onBackButtonClick}
          selectedBloodPanel={
            voucheringStateMachine.context?.selectedBloodPanel || null
          }
          applyVoucherError={voucheringStateMachine.context?.applyVoucherError}
          voucherCode={voucheringStateMachine.context?.voucherCode}
        />
      )}

      {voucheringStateMachine.matches("breakoutScreen") && (
        <Breakout
          type="Ecentric"
          onConfirm={onBreakoutDisclaimerContinue}
          redirectUrl={getPaymentRedirectUrl()}
          onDecline={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("paymentFailed") && (
        <PaymentFailure onContinue={onGoHomeButtonClick} />
      )}

      {voucheringStateMachine.matches("showingAddToProfile") && (
        <AddToProfileModal
          isModalOpen={true}
          onContinueButtonClick={onContinueButtonClick}
          onModalClose={onBackButtonClick}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {(voucheringStateMachine.matches("showingOrderSummary") ||
        voucheringStateMachine.matches("showingAddToProfile")) &&
        voucheringStateMachine.context?.paymentReceiptResponse && (
          <VoucherOrderSummary
            voucherPaymentReceipt={
              voucheringStateMachine.context.paymentReceiptResponse
            }
            onViewReceiptClick={onViewPaymentReceiptClick}
            onBackButtonClick={onBackButtonClick}
            onShareVoucherClick={onShareVoucherClick}
            onSaveToProfileClick={onSaveToProfileClick}
            isDownloadingVoucherFile={isDownloadingVoucherFile}
          />
        )}

      {voucheringStateMachine.matches("showingPaymentReceipt") &&
        voucheringStateMachine.context?.paymentReceiptResponse && (
          <PaymentReceipt
            amount={
              voucheringStateMachine.context.paymentReceiptResponse
                .paymentAmount
            }
            currency={
              voucheringStateMachine.context.paymentReceiptResponse.currencyCode
            }
            transactionDate={
              voucheringStateMachine.context.paymentReceiptResponse
                .paymentUtcDateTime
            }
            referenceNumber={
              voucheringStateMachine.context.paymentReceiptResponse
                .transactionReferenceNumber
            }
            merchantId={
              voucheringStateMachine.context.paymentReceiptResponse
                .unuMerchantId
            }
            cardNumber={
              voucheringStateMachine.context.paymentReceiptResponse.cardNumber
            }
            onBackButtonClick={onBackButtonClick}
            isDiscountApplied={
              voucheringStateMachine.context.paymentReceiptResponse
                .isDiscountApplied
            }
            originalAmount={
              voucheringStateMachine.context.paymentReceiptResponse
                .originalAmount
            }
            voucherCode={
              voucheringStateMachine.context.paymentReceiptResponse.voucherCode
            }
            voucherTypeName={
              voucheringStateMachine.context.paymentReceiptResponse
                .voucherSubTypeName
            }
            paymentMethod={
              voucheringStateMachine.context.paymentReceiptResponse
                .paymentMethod
            }
            oneVoucherDetails={
              voucheringStateMachine.context.paymentReceiptResponse
                .oneVoucherDetails
            }
          />
        )}

      {voucheringStateMachine.matches("promptingVoucherCode") &&
        voucheringStateMachine.context.applyVoucherResponse && (
          <VoucherCodeModal
            voucherDetails={voucheringStateMachine.context.applyVoucherResponse}
            isModalOpen={true}
            onContinueButtonClick={onUseVoucherNowClick}
            onModalClose={onBackButtonClick}
            onGoHomeButtonClick={onGoHomeButtonClick}
          />
        )}

      {voucheringStateMachine.matches("showingPaymentOptionsScreen") && (
        <PaymentOptionSelection
          isBuyCareCardVoucher={true}
          onBack={onBackButtonClick}
          onCardPaymentButtonClick={onCardPaymentButtonClick}
          onOneVoucherButtonClick={onOneVoucherButtonClick}
        />
      )}
      {voucheringStateMachine.matches("oneVoucherRedemptionScreen") && (
        <OneVoucherRedemption
          serviceOfferingType="Voucher"
          selectedVoucherSubType={
            voucheringStateMachine.context?.selectedVoucherSubType
          }
          responseError={
            voucheringStateMachine.context?.oneVoucherRedemptionError
          }
          onBackButtonClick={onBackButtonClick}
          onOneVoucherContinueButtonClick={onOneVoucherContinueButtonClick}
          selectedServiceAmount={
            voucheringStateMachine.context?.selectedBloodPanel
              ?.amountTotalFormatted ||
            formatAmount(
              voucheringStateMachine.context?.selectedVoucherSubType?.amount ||
                0,
              voucheringStateMachine.context?.selectedVoucherSubType
                ?.currencyCode || "ZAR"
            ) ||
            ""
          }
        />
      )}
      {voucheringStateMachine.matches("error") && (
        <Stack height="100vh" justifyContent="end">
          <DefaultError onComeBackLater={onGoHomeButtonClick}>
            <Typography variant="h2">{t("GenericError.title")}</Typography>
            <Typography pb={2}>{t("GenericError.content")}</Typography>
          </DefaultError>
        </Stack>
      )}

      {voucheringStateMachine.matches("exitToMyPurchases") && (
        <Navigate
          to="/home/more/my-purchases"
          state={{ fromMyPurchasesPage: fromMyPurchasesPage ?? 1 }}
        />
      )}

      {voucheringStateMachine.matches("exitToMyVouchers") && (
        <Navigate to="/home/more/my-carecard-vouchers" />
      )}

      {voucheringStateMachine.matches("exit") && <Navigate to="/" />}
    </>
  );
}
